import { gsap } from 'gsap';
import { PositionType } from '../../../lib/defs/types';
import {
  BlockEntity,
  despawnBlockEntity,
  despawnBlockEntityAsync,
  setBlockEntityViewLayer,
  spawnBlockEntity,
} from '../../entities/BlockEntity';
import { GameScene } from '../../GameScene';
import { mapToViewPosition } from '../../util/mapTools';
import { IEffect } from './IEffect';
import { BlockId } from '../../defs/block';
import { PowerBlockSpawnEffect } from './PowerBlockSpawnEffect';
import { Vector2 } from '../../../odie';

/*
    merge and clear blocks + spawn power block if needed
*/
export class MergeEffect implements IEffect {
  // fields
  //-------------------------------------------------------------------------
  // input
  private readonly _scene: GameScene;
  private readonly _entities: BlockEntity[];
  private readonly _target: Vector2;
  private readonly _powerBlockId?: BlockId;

  // init
  //-------------------------------------------------------------------------
  constructor(
    scene: GameScene,
    entities: BlockEntity[],
    target: PositionType,
    powerBlockId?: BlockId,
  ) {
    this._scene = scene;
    this._entities = entities;
    this._target = new Vector2(target.x, target.y);
    this._powerBlockId = powerBlockId;
  }

  // impl
  //-------------------------------------------------------------------------
  public async execute() {
    const { x, y } = mapToViewPosition(this._target);
    const baseId = this._entities[0].c.block.blockId;
    const promises: gsap.core.Tween[] = [];

    this._entities.forEach((entity) => {
      // bring to front
      setBlockEntityViewLayer(this._scene, entity, 'front');

      // tween to the merge target position
      promises.push(
        gsap.to(entity, {
          duration: 0.3,
          x,
          y,
          ease: 'back.in',
        }),
      );
    });

    await Promise.all(promises);

    const despawnPromises = this._entities.map((entity) =>
      despawnBlockEntityAsync(entity, 'mergeEffect'),
    );
    await Promise.all(despawnPromises);

    if (this._powerBlockId) {
      // Remove any block that might be at the target position, the power block will take its place
      const cell = this._scene.sessionEntity.c.map.getCellAt(this._target);
      if (cell?.base?.entity) {
        despawnBlockEntity(this._scene, cell.base.entity);
      }

      const powerBlock = spawnBlockEntity(
        this._scene,
        { id: this._powerBlockId },
        this._target,
      );

      await new PowerBlockSpawnEffect(
        powerBlock,
        baseId,
        this._powerBlockId,
      ).execute();
    }
  }
}
